<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="900" persistent scrollable>
    <v-card>
      <v-toolbar color="primary" dark dense flat>
        <v-toolbar-title>Import Completion Reports</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-4">
        <p class="text-body-2 mb-2">
          Upload a <strong>.json</strong> or <strong>.xlsx</strong> file. All records must be
          valid before importing (all-or-nothing). Need the format?
          Download a template from the Reports page.
        </p>

        <v-file-input outlined dense hide-details accept=".json,.xlsx"
          label="Import file (.json / .xlsx)" prepend-inner-icon="mdi-file-upload"
          prepend-icon="" v-model="file" @change="onFileChange" :loading="previewing" />

        <!-- Parse error -->
        <v-alert v-if="parseError" type="error" dense text class="mt-4 mb-0">
          {{ parseError }}
        </v-alert>

        <!-- Preview summary -->
        <template v-if="preview">
          <div class="d-flex align-center mt-4 mb-2" style="gap:8px">
            <v-chip small label>{{ preview.count }} record(s)</v-chip>
            <v-chip small label :color="validCount ? 'green' : 'grey'" dark>
              {{ validCount }} valid
            </v-chip>
            <v-chip small label :color="invalidCount ? 'red' : 'grey'" dark>
              {{ invalidCount }} invalid
            </v-chip>
          </div>

          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Project</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th class="text-center">Scope</th>
                  <th class="text-center">Work</th>
                  <th class="text-center">Images</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in preview.rows" :key="i"
                  :class="rowErrors(i).length ? 'red lighten-5' : ''">
                  <td>{{ i + 1 }}</td>
                  <td>{{ row.project_name }}</td>
                  <td>{{ row.date }}</td>
                  <td>{{ row.customer_id }}</td>
                  <td class="text-center">{{ row.scope_count }}</td>
                  <td class="text-center">{{ row.work_count }}</td>
                  <td class="text-center">{{ row.image_count }}</td>
                  <td>
                    <v-chip v-if="!rowErrors(i).length" x-small color="green" dark>OK</v-chip>
                    <v-tooltip v-else bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip x-small color="red" dark v-bind="attrs" v-on="on">
                          {{ rowErrors(i).length }} error(s)
                        </v-chip>
                      </template>
                      <ul class="my-1 pl-4">
                        <li v-for="(msg, m) in rowErrors(i)" :key="m">{{ msg }}</li>
                      </ul>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </template>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" :disabled="!canImport" :loading="importing" @click="doImport">
          <v-icon left>mdi-database-import</v-icon>
          Import {{ preview ? preview.count : '' }} report(s)
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import appAxios from '@/libs/app-axios'
import { LoadingPopup, ErrorPopup, SuccessPopup } from '@/libs/utils'

export default {
  name: 'CompletionReportsImportDialog',

  props: {
    value: { type: Boolean, default: false },
  },

  data() {
    return {
      file: null,
      preview: null,
      previewing: false,
      importing: false,
      parseError: '',
    }
  },

  computed: {
    validCount() {
      if (!this.preview) return 0
      return this.preview.count - this.invalidCount
    },
    invalidCount() {
      if (!this.preview) return 0
      return Object.keys(this.preview.errors || {}).length
    },
    canImport() {
      return !!this.preview && this.preview.valid && !this.importing
    },
  },

  methods: {
    reset() {
      this.file = null
      this.preview = null
      this.parseError = ''
      this.previewing = false
      this.importing = false
    },

    close() {
      this.reset()
      this.$emit('input', false)
    },

    rowErrors(i) {
      return (this.preview && this.preview.errors && this.preview.errors[i]) || []
    },

    async onFileChange(file) {
      this.preview = null
      this.parseError = ''
      if (!file) return

      try {
        this.previewing = true
        const form = new FormData()
        form.append('file', file)
        const res = await appAxios().post(
          '/v3/completion-reports/import/preview',
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        this.preview = res.data
      } catch (err) {
        this.parseError =
          err.response?.data?.message ||
          'Could not read the file. Make sure it is a valid .json or .xlsx file.'
      } finally {
        this.previewing = false
      }
    },

    async doImport() {
      if (!this.canImport || !this.file) return
      try {
        this.importing = true
        LoadingPopup.fire('Importing...')
        const form = new FormData()
        form.append('file', this.file)
        const res = await appAxios().post(
          '/v3/completion-reports/import',
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        SuccessPopup.fire(res.data?.message || 'Import complete!')
        this.$emit('imported')
        this.close()
      } catch (err) {
        ErrorPopup.fire(
          err.response?.data?.message || 'Import failed. Please review the file and try again.'
        )
      } finally {
        this.importing = false
      }
    },
  },
}
</script>
